import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ReachProSupportClient,
  ReachProSupportConversation,
} from 'src/WebApiController';

const MESSAGES_QUERY_KEY = 'reachpro-messages';

export function useReachProMessages(conversationId: string | undefined) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);
  const queryClient = useQueryClient();

  const shouldQuery =
    hasReachProSupportFeature &&
    !!conversationId &&
    activeAccountWebClientConfig.activeAccountId != null;

  const query = useQuery({
    queryKey: [MESSAGES_QUERY_KEY, conversationId],
    queryFn: async () => {
      if (!shouldQuery || !conversationId) {
        return [];
      }

      const messages = await tryInvokeApi(
        async () => {
          return await new ReachProSupportClient(
            activeAccountWebClientConfig
          ).getMessages(parseInt(conversationId));
        },
        (error) => {
          trackError('ReachProSupportClient.getMessages', error);
        }
      );

      return messages ?? [];
    },
    enabled: shouldQuery,
    refetchInterval: shouldQuery ? 60000 : false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (query.data && conversationId) {
      queryClient.setQueryData<ReachProSupportConversation[]>(
        [
          'reachpro-conversations',
          activeAccountWebClientConfig.activeAccountId,
        ],
        (old = []) =>
          old.map((conv) =>
            conv.conversationId.toString() === conversationId
              ? {
                  ...conv,
                  lastReadTimestamp: new Date().toISOString(),
                  hasUnreadMessages: false,
                }
              : conv
          )
      );
    }
  }, [
    query.dataUpdatedAt,
    conversationId,
    queryClient,
    activeAccountWebClientConfig.activeAccountId,
    query.data,
  ]);

  return query;
}
